import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const ImpersonationModal = ({ isOpen, onClose }) => {
  const { fetchUsers, startImpersonation } = useAuth();
  const { t } = useTranslation('common');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    } else {
      // Reset state when modal closes
      setUsers([]);
      setSearchTerm('');
      setSelectedUserId(null);
      setError(null);
    }
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const userList = await fetchUsers();
      setUsers(userList);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(t('impersonation.error.fetchUsers'));
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    
    const search = searchTerm.toLowerCase();
    return users.filter(user => {
      const email = (user.email || '').toLowerCase();
      const name = (user.name || '').toLowerCase();
      return email.includes(search) || name.includes(search);
    });
  }, [users, searchTerm]);

  const handleImpersonate = async () => {
    if (!selectedUserId) return;

    try {
      setImpersonating(true);
      setError(null);
      await startImpersonation(selectedUserId);
      onClose();
      // Optionally reload the page to ensure all components use the new user context
      window.location.reload();
    } catch (err) {
      console.error('Error starting impersonation:', err);
      setError(t('impersonation.error.start'));
    } finally {
      setImpersonating(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 mb-4"
                >
                  {t('impersonation.modal.title')}
                </Dialog.Title>

                {/* Search input */}
                <div className="mb-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder={t('impersonation.modal.search')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <div className="mb-4 p-3 rounded-md bg-red-50 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {/* User list */}
                <div className="mb-4 max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                  {loading ? (
                    <div className="p-8 text-center text-gray-500">
                      {t('common.loading')}
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      {searchTerm ? t('impersonation.modal.noResults') : t('impersonation.modal.noUsers')}
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {filteredUsers.map((user) => (
                        <li
                          key={user.id || user.email}
                          className={`p-3 cursor-pointer hover:bg-gray-50 ${
                            selectedUserId === (user.id || user.email)
                              ? 'bg-blue-50 border-l-4 border-blue-500'
                              : ''
                          }`}
                          onClick={() => setSelectedUserId(user.id || user.email)}
                        >
                          <div className="flex items-center">
                            {user.picture ? (
                              <img
                                src={user.picture}
                                alt={user.name || user.email}
                                className="h-10 w-10 rounded-full mr-3"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center mr-3">
                                <span className="text-gray-600 text-sm font-medium">
                                  {(user.name || user.email || 'U')[0].toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {user.name || user.email}
                              </p>
                              {user.name && (
                                <p className="text-sm text-gray-500 truncate">
                                  {user.email}
                                </p>
                              )}
                            </div>
                            {selectedUserId === (user.id || user.email) && (
                              <div className="ml-2">
                                <svg
                                  className="h-5 w-5 text-blue-500"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={onClose}
                    disabled={impersonating}
                  >
                    {t('impersonation.modal.cancel')}
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleImpersonate}
                    disabled={!selectedUserId || impersonating}
                  >
                    {impersonating ? t('common.loading') : t('impersonation.modal.impersonate')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ImpersonationModal;


